import React, { useEffect, useState, useContext } from 'react';
import { Typography, theme, Layout, Button, Tooltip, notification, Affix, Card, Row, Col } from 'antd';
import { AudioOutlined, CheckCircleTwoTone, CloseCircleTwoTone, QuestionCircleFilled, QuestionCircleOutlined, UploadOutlined, TeamOutlined } from '@ant-design/icons';
import { FaFileZipper } from 'react-icons/fa6';
import { MdDashboard } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from '../../context/ThemeContext';
import { useDispatch, useSelector } from 'react-redux';
import { getApplicationUserByIdentityUserId } from '../../actions/applicationUserActions';
import dayjs from '../../utils/dayjs';
import FileUploaderAndViewer from '../../components/uploader/FileUploader.component';
import NiFiFlowMonitor from '../../components/nifi/NiFiFlowMonitor.component';
import PageWrapper from '../../components/common/PageWrapper';

const { Text } = Typography;
const { Content } = Layout;

const DashboardScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [greeting, setGreeting] = useState('Good morning');
  const { themeConfig } = useContext(ThemeContext);
  const [api, contextHolder] = notification.useNotification();
  const [isRecording, setIsRecording] = useState(false);
  
  const { 
    token: { 
      colorPrimary,
      colorBgContainer,
      colorBgElevated,
      colorTextSecondary
    }
  } = theme.useToken();

  // Redux state
  const { userInfo } = useSelector((state) => state.userLogin);
  const { applicationUser } = useSelector((state) => state.applicationUserDetails);

  useEffect(() => {
    // Load applicationUser data when component mounts
    if (userInfo?.userId) {
      dispatch(getApplicationUserByIdentityUserId(userInfo.userId));
    }
  }, [dispatch, userInfo?.userId]);

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour >= 12 && hour < 17) {
      setGreeting('Good afternoon');
    } else if (hour >= 17) {
      setGreeting('Good evening');
    }
  }, []);

  const openNotification = (placement, type, description) => {
    api.info({
      message: type,
      description: description,
      placement,
      icon: type === t('Success') ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : <CloseCircleTwoTone twoToneColor="#eb2f96" />,
    });
  };

  const cards = [
    {
      title: 'New Upload',
      icon: <UploadOutlined style={{ fontSize: 24 }} />,
      description: 'Upload new patient data',
      onClick: () => navigate('/upload/new'),
      color: '#4F46E5',
    },
    {
      title: 'All Uploads',
      icon: <FaFileZipper style={{ fontSize: 24 }} />,
      description: 'View all uploaded datasets',
      onClick: () => navigate('/uploads'),
      color: '#E11D48',
    },
    // Add more cards here based on Scenario.md
  ];

  return (
    <PageWrapper
      title="Dashboard"
      subtitle={
        <Text type="secondary" style={{ fontSize: '14px' }}>
          {greeting}{applicationUser?.firstName ? `, ${applicationUser.firstName}` : userInfo?.userName ? `, ${userInfo.userName}` : '!'}
        </Text>
      }
      icon={<MdDashboard size={24} />}
      showCard={false}
    >
      {contextHolder}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={8} lg={8}>
          <FileUploaderAndViewer />
        </Col>
        <Col xs={24} sm={24} md={16} lg={16}>
          <NiFiFlowMonitor />
        </Col>
        {cards.slice(2).map((card, idx) => (
          <Col xs={24} sm={12} md={8} lg={8} key={idx}>
            <Card
              hoverable
              onClick={card.onClick}
              style={{ cursor: 'pointer', height: '100%' }}
              bodyStyle={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12, padding: 16 }}
            >
              <div style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                backgroundColor: `${card.color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: card.color
              }}>{card.icon}</div>
              <Text strong style={{ fontSize: 14 }}>{card.title}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>{card.description}</Text>
            </Card>
          </Col>
        ))}
      </Row>
    </PageWrapper>
  );
};

export default DashboardScreen;
